import { RefObject, useEffect } from 'react';
import { css } from '@emotion/react';
import { CalloutBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { Button, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { useOperatoryAvailabilityCallout } from './hooks/use-operatory-availability-state';

export const OperatoryAvailabilityCallout = ({
  shouldOpenCallout,
  setOpenCallout,
  anchorRef,
  onMouseEnter,
  onMouseLeave,
}: {
  shouldOpenCallout: boolean;
  setOpenCallout: (open: boolean) => void;
  anchorRef: RefObject<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
}) => {
  const { t } = useTranslation('schedule');
  const {
    isCalloutDismissed,
    dismissCallout,
    shouldShowCallout,
    signUpCallout,
    isOperatoryAvailabilityEnabled,
    viewAvailabilitySettings,
  } = useOperatoryAvailabilityCallout();

  const {
    getDialogProps,
    open,
    close,
    refs: popoverRefs,
  } = usePopoverDialog({
    placement: 'bottom',
  });

  // Set anchor reference for popover positioning
  useEffect(() => {
    if (anchorRef.current) {
      popoverRefs.setReference(anchorRef.current);
    }
  }, [anchorRef, popoverRefs]);

  // Open popover when banner should be shown
  useEffect(() => {
    if (shouldShowCallout) {
      open();
    }
  }, [shouldShowCallout, open]);

  const onPrimaryActionClick = () => {
    if (isOperatoryAvailabilityEnabled) {
      viewAvailabilitySettings();
    } else {
      signUpCallout();
    }
    setOpenCallout(false);
  };

  const onDismiss = () => {
    dismissCallout();
    setOpenCallout(false);
    close();
  };

  if (isCalloutDismissed || !shouldShowCallout || !shouldOpenCallout) {
    return null;
  }

  return (
    <PopoverDialog {...getDialogProps()} css={dialogStyles} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <CalloutBanner
        color='seaweed'
        arrowPosition='top'
        badge='new'
        title={
          <Text size='medium' weight='bold'>
            {isOperatoryAvailabilityEnabled
              ? t('Operatory Availability is live!')
              : t('Operatory View is officially live!')}
          </Text>
        }
        cardStyles={{ width: '340px' }}
        description={
          <Text size='medium'>
            {isOperatoryAvailabilityEnabled
              ? t(
                  'To finish setup, go to Availability Settings, change “Availability Calculated By” to Operatory, and review your availability.'
                )
              : t('Operatory Availability is up next — sign up to be the first to try it')}
          </Text>
        }
        actions={
          <>
            <Button variant='tertiary' css={{ color: 'var(--fabric-color-neutral-60)' }} onClick={onDismiss}>
              {t('Done')}
            </Button>
            <Button variant='tertiary' color='primary' onClick={onPrimaryActionClick}>
              {isOperatoryAvailabilityEnabled ? t('View Settings') : t('Sign Up')}
            </Button>
          </>
        }
      />
    </PopoverDialog>
  );
};

const dialogStyles = css({
  width: '350px',
  padding: 0,
  boxShadow: 'none',
  backgroundColor: 'transparent',
});
